import { useCallback, useState } from 'react';
import mapboxGeocodingService, {
  MapboxFeature,
  GeocodingOptions,
} from 'services/mapboxGeocoding';

interface UseMapboxGeocodingState {
  results: MapboxFeature[];
  isLoading: boolean;
}

interface UseMapboxGeocodingReturn extends UseMapboxGeocodingState {
  searchAddresses: (
    searchString: string,
    options?: GeocodingOptions,
  ) => Promise<void>;
  searchAddressesOnly: (
    searchString: string,
    options?: Omit<GeocodingOptions, 'types'>,
  ) => Promise<void>;
  searchPlaces: (
    searchString: string,
    options?: Omit<GeocodingOptions, 'types'>,
  ) => Promise<void>;
  reverseGeocode: (
    longitude: number,
    latitude: number,
    options?: Omit<GeocodingOptions, 'proximity' | 'bbox'>,
  ) => Promise<void>;
  clearResults: () => void;
}

const useMapboxGeocoding = (): UseMapboxGeocodingReturn => {
  const [state, setState] = useState<UseMapboxGeocodingState>({
    results: [],
    isLoading: false,
  });

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  const setResults = useCallback((results: MapboxFeature[]) => {
    setState((prev) => ({ ...prev, results }));
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
  }, [setResults]);

  const searchAddresses = useCallback(
    async (searchString: string, options?: GeocodingOptions) => {
      if (!searchString.trim()) {
        clearResults();
        return;
      }

      setLoading(true);

      try {
        const response = await mapboxGeocodingService.searchAddresses(
          searchString,
          options,
        );
        setResults(response.features);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setResults, clearResults],
  );

  const searchAddressesOnly = useCallback(
    async (searchString: string, options?: Omit<GeocodingOptions, 'types'>) => {
      if (!searchString.trim()) {
        clearResults();
        return;
      }

      setLoading(true);

      try {
        const response = await mapboxGeocodingService.searchAddressesOnly(
          searchString,
          options,
        );
        setResults(response.features);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setResults, clearResults],
  );

  const searchPlaces = useCallback(
    async (searchString: string, options?: Omit<GeocodingOptions, 'types'>) => {
      if (!searchString.trim()) {
        clearResults();
        return;
      }

      setLoading(true);

      try {
        const response = await mapboxGeocodingService.searchPlaces(
          searchString,
          options,
        );
        setResults(response.features);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setResults, clearResults],
  );

  const reverseGeocode = useCallback(
    async (
      longitude: number,
      latitude: number,
      options?: Omit<GeocodingOptions, 'proximity' | 'bbox'>,
    ) => {
      setLoading(true);

      try {
        const response = await mapboxGeocodingService.reverseGeocode(
          longitude,
          latitude,
          options,
        );
        setResults(response.features);
      } catch (error) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setResults],
  );

  return {
    results: state.results,
    isLoading: state.isLoading,
    searchAddresses,
    searchAddressesOnly,
    searchPlaces,
    reverseGeocode,
    clearResults,
  };
};

export default useMapboxGeocoding;
